import React, { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { 
  DeleteButton,
  ExportButton,
  FormButton,
  InfoButton,
  copy,
  deletionAlert,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../_helpers/Helpers";
import * as actions from "../../_redux/courses/CoursesAction";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { CoursesFilter } from "./courses-filter/CoursesFilter";
import { CoursesTable } from "./courses-table/CoursesTable";
import { useCoursesUIContext } from "./CoursesUIContext";

export function CoursesCard() {
  // Courses UI Context
  const coursesUIContext = useCoursesUIContext();
  const coursesUIProps = useMemo(() => {
    return {
      ids: coursesUIContext.ids,
      setIds: coursesUIContext.setIds,
      queryParams: coursesUIContext.queryParams,
      setQueryParams: coursesUIContext.setQueryParams,
      openNewParentCoursePage: coursesUIContext.openNewParentCoursePage,
      openNewSubCoursePage: coursesUIContext.openNewSubCoursePage
    };
  }, [coursesUIContext]);
    
  // Getting curret state of Course list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.courses }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, parentCourse, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isAdditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);

  // Courses Redux state
  const dispatch = useDispatch();
  
  // Kimlik, Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Ders Listesi Sayfasını Açar */
  const openCourseListPage = () => {
    const newQueryParams = { 
      filter: {},
      sortOrder: "asc", // asc||desc
      sortField: "name",
      pageNumber: 1,
      pageSize: 10
    };
    coursesUIProps.setQueryParams(newQueryParams);
  }

  /** Belli Bir Ders Listesini Excele Aktarır */
  const exportCoursesToExcel = () => {
    dispatch(actions.getAllCourses(coursesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [45]
      const columns = ["Adı"];
      entities.forEach(entity => rows.push([entity.name]));
      const filename = parentCourse ? parentCourse + " Alt Ders Listesi" : "Ders Listesi";
      const sheetname = filename.length <= 31 ? filename : "Ders Listesi";
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });    
  }

  /** Belli Bir Ders Listesini PDF e Aktarır */
  const exportCoursesToPDF = () => {
    dispatch(actions.getAllCourses(coursesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "Adı",
          rows: entities.map(entity => entity.name)
        }
      ];
  
      const filename = parentCourse ? parentCourse + " Alt Ders Listesi" : "Ders Listesi";
      exportToPDF(views, filename);
    });    
  }

  /** Belli Bir Ders Listesini Yazdırma Penceresini Açar */
  const printCourses = () => {
    dispatch(actions.getAllCourses(coursesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const properties = ["Adı"];
      const header = parentCourse ? parentCourse + " Alt Ders Listesi" : "Ders Listesi";
  
      entities.forEach(entity => {
        data.push({
          "Adı": entity.name
        });
      });
  
      print({ data, properties, header });
    });
  }

  /** Belli Bir Ders Listesini Panoya Kopyalar */
  const copyCourses = () => {
    dispatch(actions.getAllCourses(coursesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["Adı"];
      const header = parentCourse ? parentCourse + " Alt Ders Listesi" : "Ders Listesi";
      entities.forEach(entity => rows.push([entity.name]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Ders Listesini Siler */
  const deleteCourses = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }
  
    deletionAlert().then(result => {
      if (result.isConfirmed) dispatch(actions.deleteCourses(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
  
    if (isSuccess) {
      setTimeout(() => {
        // refresh list after deletion
        dispatch(actions.getCourses(coursesUIProps.queryParams));
        // clear selections list
        coursesUIProps.setIds([]);
      }, 2500);
    }
  };
  
  const sub = coursesUIProps.queryParams.filter.parentId ? "Alt" : "";
  const title = <>{sub} Ders Listesi
                  {coursesUIProps.queryParams.filter.parentId ? 
                  <> (Ana Ders: <span style={{color:"red"}}>{parentCourse}</span>)</>: ""}
                  {coursesUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{coursesUIProps.ids.length}</span>]</> : ""}
                </>;

  const openNewCoursePage = coursesUIProps.queryParams.filter.parentId ? 
                            () => coursesUIProps.openNewSubCoursePage(coursesUIProps.queryParams.filter.parentId) : 
                            coursesUIProps.openNewParentCoursePage;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (coursesUIProps.queryParams.filter.parentId || isExportationAuthorized || isAdditionAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              coursesUIProps.queryParams.filter.parentId &&
              <InfoButton
                path="Navigation/Arrow-left"
                title="Ders Listesi"
                method={openCourseListPage}
              />
            }
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportCoursesToExcel}
                exportToPDF={exportCoursesToPDF}
                print={printCourses}
                copy={copyCourses}
              />
            }
            {
              isAdditionAuthorized &&
              <FormButton 
                path="Navigation/Plus"
                title={"Yeni " + sub + " Ders"}
                method={openNewCoursePage}
              />
            }              
            {
              isDeletionAuthorized && 
              <DeleteButton
                title="Seçilenleri Sil"
                method={() => deleteCourses(coursesUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <CoursesFilter />
        <CoursesTable
          deleteCourses={deleteCourses}
        />
      </CardBody>
    </Card>
  );
}